/* Modal de configuração de um custo.
 *
 * Armadilha conhecida, corrigida por construção: na versão anterior o modal
 * era um elemento único e reaproveitado, e as faixas de um campo vazavam para
 * o campo seguinte. Este formulário só existe enquanto o modal está aberto e
 * é remontado a cada abertura pela `key` (ver o Dialog no index), então abrir
 * é sempre começar do estado do campo escolhido. */

import { custoConfig } from '@favo/core';
import { Plus, X } from 'lucide-react';
import * as React from 'react';

import { CampoMoeda } from '@/componentes/campo-moeda';
import { Camada } from '@/componentes/celula';
import { Segmentado } from '@/componentes/segmentado';
import { Button } from '@/componentes/ui/button';
import {
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/componentes/ui/dialog';
import { Input } from '@/componentes/ui/input';
import { RotuloValor } from '@/componentes/valor';
import type { ConfigCusto, ModoCusto, SubModoCusto, VariavelFaixa } from '@/dados';
import { formatarNumero } from '@/design/formato';

import {
  MODOS,
  SUB_MODOS,
  VARIAVEIS,
  descreverExemplo,
  faixasIniciais,
  montarConfig,
  ordenarFaixas,
  taxaInicial,
  type FaixaEdicao,
} from './configuracao';

export function FormularioCusto({
  titulo,
  cfg,
  configurado,
  kwpRef,
  modulosRef,
  aoAplicar,
  aoRemover,
  aoCancelar,
}: {
  titulo: string;
  cfg: ConfigCusto;
  configurado: boolean;
  kwpRef: number | null;
  modulosRef: number | null;
  aoAplicar: (cfg: ConfigCusto) => Promise<boolean>;
  aoRemover: () => Promise<boolean>;
  aoCancelar: () => void;
}) {
  const [modo, setModo] = React.useState<ModoCusto>(cfg.modo ?? 'fixo');
  const [taxa, setTaxa] = React.useState<number | null>(taxaInicial(cfg));
  // `||` e não `??`: submodo gravado como texto vazio por versão antiga da tela
  // tem de cair no padrão, igual a ausente. Mesma leitura do core.
  const [subModo, setSubModo] = React.useState<SubModoCusto>(cfg.subModo || 'modulo');
  const [variavel, setVariavel] = React.useState<VariavelFaixa>(cfg.faixaVariavel || 'kwp');
  const [faixas, setFaixas] = React.useState<FaixaEdicao[]>(() => faixasIniciais(cfg));
  const [erro, setErro] = React.useState<string | null>(null);
  const [ocupado, setOcupado] = React.useState(false);

  const unidade = variavel === 'modulos' ? 'módulos' : 'kWp';
  const emEdicao = montarConfig({ modo, taxa, subModo, variavel, faixas });
  const previa = custoConfig(emEdicao, kwpRef ?? 0, modulosRef ?? 0);

  function alterarFaixa(id: string, mudanca: Partial<FaixaEdicao>) {
    setFaixas((atual) => atual.map((f) => (f.id === id ? { ...f, ...mudanca } : f)));
  }

  function adicionarFaixa() {
    setFaixas((atual) => {
      const limites = atual.map((f) => f.ate).filter((n): n is number => n !== null);
      const passo = variavel === 'modulos' ? 10 : 5;
      const nova: FaixaEdicao = {
        id: crypto.randomUUID(),
        ate: (limites.length > 0 ? Math.max(...limites) : 0) + passo,
        taxa: null,
      };
      const indiceAberta = atual.findIndex((f) => f.ate === null);
      const lista = atual.slice();
      lista.splice(indiceAberta >= 0 ? indiceAberta : lista.length, 0, nova);
      // Sem faixa aberta, um sistema maior que o último degrau ficaria sem
      // taxa. A última é sempre "acima de".
      if (!lista.some((f) => f.ate === null)) {
        lista.push({ id: crypto.randomUUID(), ate: null, taxa: null });
      }
      return lista;
    });
  }

  function removerFaixa(id: string) {
    setFaixas((atual) => {
      if (atual.length <= 1) return atual;
      const lista = atual.filter((f) => f.id !== id);
      const ultima = lista[lista.length - 1];
      if (ultima && !lista.some((f) => f.ate === null)) ultima.ate = null;
      return lista;
    });
  }

  async function aplicar() {
    if (modo === 'faixa') {
      const limites = faixas
        .map((f) => f.ate)
        .filter((n): n is number => n !== null)
        .sort((a, b) => a - b);
      for (let i = 1; i < limites.length; i++) {
        if ((limites[i] ?? 0) <= (limites[i - 1] ?? 0)) {
          setErro('Duas faixas terminam no mesmo limite. Ajuste os valores para que cresçam.');
          return;
        }
      }
    }

    setErro(null);
    setOcupado(true);
    const ok = await aoAplicar(emEdicao);
    setOcupado(false);
    if (!ok) setErro('Não foi possível salvar. Verifique a conexão e tente de novo.');
  }

  async function remover() {
    setErro(null);
    setOcupado(true);
    const ok = await aoRemover();
    setOcupado(false);
    if (!ok) setErro('Não foi possível remover. Verifique a conexão e tente de novo.');
  }

  return (
    <>
      <DialogHeader>
        <DialogTitle>Configurar {titulo}</DialogTitle>
        <DialogDescription>
          Escolha como este custo é calculado a partir dos dados de cada projeto.
        </DialogDescription>
      </DialogHeader>

      <div className="grid gap-4">
        <Segmentado
          rotulo="Cálculo"
          opcoes={MODOS.map((m) => ({ valor: m.valor, rotulo: m.rotulo }))}
          valor={modo}
          aoMudar={setModo}
        />

        {modo === 'faixa' ? (
          <div className="grid gap-4">
            <div className="flex flex-wrap gap-6">
              <Segmentado
                rotulo="Definir faixas por"
                opcoes={VARIAVEIS.map((v) => ({ valor: v.valor, rotulo: v.rotulo }))}
                valor={variavel}
                aoMudar={setVariavel}
                tamanho="pequeno"
              />
              <Segmentado
                rotulo="Calcular cada faixa por"
                opcoes={SUB_MODOS.map((s) => ({ valor: s.valor, rotulo: s.rotulo }))}
                valor={subModo}
                aoMudar={setSubModo}
                tamanho="pequeno"
              />
            </div>

            <div className="grid gap-2">
              {faixas.map((faixa, indice) => {
                const de = indice === 0 ? 0 : (faixas[indice - 1]?.ate ?? 0);
                return (
                  <Camada
                    key={faixa.id}
                    className="flex items-center gap-2 px-3 py-2"
                  >
                    <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5 text-nota">
                      {faixa.ate === null ? (
                        <span>
                          Acima de <b>{formatarNumero(de)}</b> {unidade}
                        </span>
                      ) : (
                        <>
                          <span>{de === 0 ? 'Até' : `De ${formatarNumero(de)} a`}</span>
                          <Input
                            type="number"
                            min={0.1}
                            step={variavel === 'modulos' ? 1 : 0.5}
                            value={faixa.ate}
                            aria-label={`Limite da faixa em ${unidade}`}
                            className="numero bg-card h-8 w-20 text-right"
                            onChange={(evento) => {
                              const n = parseFloat(evento.currentTarget.value);
                              alterarFaixa(faixa.id, { ate: Number.isFinite(n) ? n : null });
                            }}
                            onBlur={() => setFaixas((atual) => ordenarFaixas(atual))}
                          />
                          <span>{unidade}</span>
                        </>
                      )}
                    </div>

                    <CampoMoeda
                      valor={faixa.taxa}
                      aoMudar={(valor) => alterarFaixa(faixa.id, { taxa: valor })}
                      aria-label="Taxa da faixa"
                      className="w-36 shrink-0"
                      classeControle="h-8 bg-card"
                    />

                    <Button
                      variant="ghost"
                      size="icon-sm"
                      aria-label="Remover faixa"
                      disabled={faixas.length <= 1}
                      onClick={() => removerFaixa(faixa.id)}
                    >
                      <X />
                    </Button>
                  </Camada>
                );
              })}

              <div>
                <Button variant="outline" size="sm" onClick={adicionarFaixa}>
                  <Plus />
                  Adicionar faixa
                </Button>
              </div>
            </div>
          </div>
        ) : (
          <CampoMoeda
            rotulo={modo === 'fixo' ? 'Valor por projeto' : 'Taxa'}
            valor={taxa}
            aoMudar={setTaxa}
            sufixo={modo === 'modulo' ? '/mód.' : modo === 'kwp' ? '/kWp' : null}
          />
        )}

        <Camada className="bg-areia px-4 py-3">
          <RotuloValor
            rotulo="Neste exemplo"
            valor={previa}
            formato="moeda"
            tamanho="sub"
            nota={descreverExemplo(modo, variavel, kwpRef, modulosRef)}
          />
        </Camada>

        {erro ? (
          <p role="alert" className="text-destructive text-apoio">
            {erro}
          </p>
        ) : null}
      </div>

      <DialogFooter className="sm:justify-between">
        {configurado ? (
          <Button variant="ghost" onClick={() => void remover()} disabled={ocupado}>
            Remover configuração
          </Button>
        ) : (
          <span />
        )}
        <div className="flex gap-2">
          <Button variant="outline" onClick={aoCancelar} disabled={ocupado}>
            Cancelar
          </Button>
          <Button onClick={() => void aplicar()} disabled={ocupado}>
            {ocupado ? 'Salvando…' : 'Aplicar'}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}
